import React, { useState, useEffect } from 'react';
import { authAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';

/**
 * ChangePasswordDialog
 * - In "forced" mode (user.must_change_password), it opens automatically and cannot be dismissed.
 *   Current password is not required.
 * - In "normal" mode, current password is required.
 */
const ChangePasswordDialog = ({ open, onOpenChange, forced }) => {
  const { checkAuth } = useAuth();
  const [form, setForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm({ current_password: '', new_password: '', confirm_password: '' });
  }, [open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.new_password.length < 6) { toast.error('New password must be at least 6 characters'); return; }
    if (form.new_password !== form.confirm_password) { toast.error('Passwords do not match'); return; }
    setSaving(true);
    try {
      await authAPI.changePassword({
        current_password: forced ? undefined : form.current_password,
        new_password: form.new_password,
      });
      toast.success('Password updated');
      await checkAuth();
      onOpenChange(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update password');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={forced ? () => {} : onOpenChange}>
      <DialogContent className="sm:max-w-md" onInteractOutside={forced ? (e) => e.preventDefault() : undefined}>
        <DialogHeader>
          <DialogTitle className="font-heading">{forced ? 'Set Your Password' : 'Change Password'}</DialogTitle>
          <DialogDescription>
            {forced
              ? 'Your supervisor created this login for you. Please set your own password before continuing.'
              : 'Pick a new password to secure your account.'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          {!forced && (
            <div className="space-y-2">
              <Label>Current Password *</Label>
              <Input type="password" value={form.current_password} onChange={(e) => setForm({ ...form, current_password: e.target.value })} className="rounded-xl" required data-testid="current-password" />
            </div>
          )}
          <div className="space-y-2">
            <Label>New Password * <span className="text-xs text-muted-foreground">(minimum 6 characters)</span></Label>
            <Input type="password" value={form.new_password} onChange={(e) => setForm({ ...form, new_password: e.target.value })} className="rounded-xl" required minLength={6} data-testid="new-password" />
          </div>
          <div className="space-y-2">
            <Label>Confirm New Password *</Label>
            <Input type="password" value={form.confirm_password} onChange={(e) => setForm({ ...form, confirm_password: e.target.value })} className="rounded-xl" required minLength={6} data-testid="confirm-password" />
          </div>
          <div className="flex gap-3 pt-4">
            {!forced && (
              <Button type="button" variant="outline" className="flex-1 rounded-xl" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
            )}
            <Button type="submit" className="flex-1 rounded-xl" disabled={saving} data-testid="save-password-btn">
              {saving ? 'Saving...' : forced ? 'Set Password & Continue' : 'Update Password'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ChangePasswordDialog;
